import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { getFloor, type Floor, type FloorCell } from './api'
import { useElection } from './useElection'
import {
  Attention, CellCard, Countdown, Escalations, Panel, Stat, Stations,
} from './floor/rails'
import {
  STRIP, TRACK, drawStrip, drawTrack, hitMote, hourAt, readTheme, step,
  type Mote, type Theme, type TrackScene,
} from './floor/track'

/**
 * The Floor — the station-mates console.
 *
 * ## Why this console moves and the other three do not
 *
 * A dossier, an estimate and an archive hold still, so their boards hold still: motion over
 * them would be decoration pretending to be information. Polling day is the one surface in this
 * platform where the subject is changing while somebody watches it, where the useful quantity
 * is a RATE, and where a still frame cannot distinguish a cell that is climbing from one that
 * stopped at ten in the morning. Here the animation carries the fact.
 *
 * ## The whole day is in one payload, which is what makes it scrubbable
 *
 * Every cell ships its cumulative count per hour. Dragging the strip or pressing play moves an
 * INDEX, not a request — so the board replays at sixty frames a second with the network idle,
 * and the reader can go back to eleven o'clock, see which cells were already stalling, and come
 * forward again. Fetching per hour would put a round trip inside a drag, which is the
 * difference between a scrubber and a slideshow.
 *
 * ## Three things the board refuses to do
 *
 * **It will not show the wall clock as the state.** The board is drawn at the last REPORT, and
 * says so. A screen showing "now" while its newest fact is forty minutes old draws a reporting
 * blackout as calm — which is the exact failure this module exists to prevent.
 *
 * **It will not treat a full queue as a slow cell.** People standing at the station are drawn
 * as a ring inside the dot, not as an absence. A cell that is waiting and a cell that has not
 * left home need opposite responses, and one number cannot carry both.
 *
 * **It will not keep shouting after the close.** A cell badly behind at two in the afternoon is
 * an emergency; the same cell at six is a result. The critical ink is conditional on there
 * being time left to use it.
 */
export function FloorPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { electionId } = useElection()
  const [floor, setFloor] = useState<Floor | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [hour, setHour] = useState<number | null>(null)
  const [playing, setPlaying] = useState(false)
  const [showTrails, setShowTrails] = useState(true)
  const [live, setLive] = useState(true)
  const [stationId, setStationId] = useState<string | null>(params.get('station'))
  const [selected, setSelected] = useState<string | null>(null)
  const [hovered, setHovered] = useState<string | null>(null)
  const [immersive, setImmersive] = useState(false)

  // True once the first payload has landed and the canvases are actually in the DOM.
  const ready = floor !== null

  const rootRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLCanvasElement>(null)
  const stripRef = useRef<HTMLCanvasElement>(null)
  const themeRef = useRef<Theme | null>(null)
  const motesRef = useRef<Map<string, Mote>>(new Map())
  const sceneRef = useRef<TrackScene | null>(null)
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 })
  const hourRef = useRef<number | null>(null)
  const playRef = useRef(false)
  const reduced = useRef(false)

  // ── data ───────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!electionId) return
    try {
      setError(null)
      setFloor(await getFloor(electionId))
    } catch (err) {
      setError(explain(err))
    }
  }, [electionId])

  useEffect(() => { void load() }, [load])

  /**
   * The live poll.
   *
   * Thirty seconds while the board is live and the reader has not scrubbed. A refresh that
   * arrived while somebody was dragging through the morning would yank them back to the
   * present mid-gesture, so scrubbing turns the poll off and the header says the board is held.
   */
  useEffect(() => {
    if (!live || !floor?.day.pollsOpen) return
    const id = window.setInterval(() => void load(), 30_000)
    return () => window.clearInterval(id)
  }, [live, floor?.day.pollsOpen, load])

  const hours = floor?.hours ?? []
  const lastHour = Math.max(0, hours.length - 1)
  const index = hour ?? lastHour
  hourRef.current = index
  playRef.current = playing

  /** Cumulative voted per cell at the scrubbed hour — the whole reason the day ships inline. */
  const votedAt = useMemo(() => {
    const out = new Map<string, number>()
    for (const cell of floor?.cells ?? []) {
      out.set(cell.id, cell.series[index] ?? cell.voted)
    }
    return out
  }, [floor, index])

  const visible = useMemo(
    () => (floor?.cells ?? []).filter((c) => !stationId || c.stationId === stationId),
    [floor, stationId],
  )
  const missed = useMemo(() => {
    if (!stationId || !floor) return null
    const out = new Set<string>()
    for (const cell of floor.cells) if (cell.stationId !== stationId) out.add(cell.id)
    return out
  }, [floor, stationId])

  /**
   * Where the pace rule sits at the scrubbed hour.
   *
   * Recomputed from the hour rather than taken from `day.elapsedShare`, which describes the
   * moment the payload was built. Scrubbing back to eleven with the rule still at the close
   * would tell the reader every cell was behind all day.
   */
  const paceShare = useMemo(() => {
    if (!floor || hours.length === 0) return 0
    const opens = Date.parse(floor.day.opensAt)
    const closes = Date.parse(floor.day.closesAt)
    const at = Date.parse(hours[index] ?? floor.day.asAt)
    return Math.max(0, Math.min(1, (at - opens) / Math.max(1, closes - opens)))
  }, [floor, hours, index])

  // ── the loop ───────────────────────────────────────────────────────────────

  useEffect(() => {
    const root = rootRef.current
    const stage = stageRef.current
    const track = trackRef.current
    const strip = stripRef.current
    if (!root || !stage || !track || !strip) return

    themeRef.current = readTheme(root)
    reduced.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const resize = () => {
      const rect = stage.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const w = Math.max(80, rect.width)
      const h = Math.max(80, rect.height)
      sizeRef.current = { w, h, dpr }
      track.width = w * dpr
      track.height = h * dpr
      track.style.width = `${w}px`
      track.style.height = `${h}px`
      const sh = strip.clientHeight || 110
      strip.width = w * dpr
      strip.height = sh * dpr
      strip.style.width = `${w}px`
    }
    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(stage)

    let raf = 0
    let last = performance.now()
    let sinceStep = 0
    let failed = false

    const frame = (now: number) => {
      // Re-armed FIRST. With the request at the end, one exception in a draw call freezes the
      // board permanently on a still image — the worst possible failure for a screen somebody
      // is running an operation from.
      raf = requestAnimationFrame(frame)
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now

      const scene = sceneRef.current
      const ctx = track.getContext('2d')
      const sctx = strip.getContext('2d')
      if (!scene || !ctx) return
      // Read the live size every frame rather than trusting the snapshot the scene was built
      // with: the scene is rebuilt on data, the canvas is resized by an observer, and the two
      // do not happen in a fixed order.
      scene.width = sizeRef.current.w
      scene.height = sizeRef.current.h
      scene.dpr = sizeRef.current.dpr
      scene.now = now
      try {
        step(motesRef.current, scene, reduced.current ? 1 : dt)
        drawTrack(ctx, scene, motesRef.current)
        if (sctx && floorRef.current) {
          drawStrip(sctx, floorRef.current.series, floorRef.current.hours,
            hourRef.current ?? 0, scene.width, strip.clientHeight, scene.dpr, scene.theme, now)
        }
      } catch (err) {
        if (!failed) {
          failed = true
          console.error('[floor] frame failed; the loop continues', err)
        }
      }

      // The replay advances an hour every 900ms, which is slow enough to read a lane and fast
      // enough that the whole day is under ten seconds.
      if (playRef.current) {
        sinceStep += dt * 1000
        if (sinceStep > 900) {
          sinceStep = 0
          const total = floorRef.current?.hours.length ?? 0
          const next = (hourRef.current ?? 0) + 1
          if (next >= total) {
            setPlaying(false)
          } else {
            setHour(next)
          }
        }
      }
    }
    raf = requestAnimationFrame(frame)

    const onTheme = () => { themeRef.current = readTheme(root) }
    const watch = new MutationObserver(onTheme)
    watch.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
      watch.disconnect()
    }
    // Two dependencies, both STRUCTURAL rather than data.
    //
    // Until the first payload lands the page renders a loading block and there is no canvas in
    // the DOM at all, so this effect returns early. With no dependency that changes on arrival
    // it would never run again and the board would stay permanently blank — which is exactly
    // what happened the first time. Everything else the loop needs (the data, the scrubbed
    // hour, the pointer, the size) reaches it through refs, so a poll never tears the animation
    // down and restarts every trail from nothing.
    //
    // `immersive` because full screen moves the board into a portal, and React unmounts and
    // remounts the subtree when its parent changes that way — the canvas refs then point at
    // NEW elements and the loop has to re-bind or it draws into a canvas nobody can see.
  }, [ready, immersive])

  // The loop reads these without being rebuilt.
  const floorRef = useRef<Floor | null>(null)
  floorRef.current = floor

  useEffect(() => {
    const theme = themeRef.current
    if (!floor || !theme) return
    sceneRef.current = {
      cells: floor.cells,
      stations: floor.stations,
      votedAt,
      paceShare,
      width: sizeRef.current.w,
      height: sizeRef.current.h,
      dpr: sizeRef.current.dpr,
      theme,
      now: performance.now(),
      hovered,
      selected,
      missed,
      showTrails: showTrails && !reduced.current,
      replaying: playing,
    }
  }, [floor, votedAt, paceShare, hovered, selected, missed, showTrails, playing])

  // ── interaction ────────────────────────────────────────────────────────────

  const onTrackMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const hit = hitMote(motesRef.current, e.clientX - rect.left, e.clientY - rect.top)
    if (hit !== hovered) setHovered(hit)
  }

  const onTrackClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const hit = hitMote(motesRef.current, e.clientX - rect.left, e.clientY - rect.top)
    setSelected(hit === selected ? null : hit)
  }

  /** Drag anywhere on the strip to scrub. Holding the day still is what stops the poll. */
  const onScrub = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    const total = hours.length
    const move = (ev: MouseEvent | React.MouseEvent) => {
      setHour(hourAt(('clientX' in ev ? ev.clientX : 0) - rect.left, sizeRef.current.w, total))
      setPlaying(false)
      setLive(false)
    }
    move(e)
    const onMove = (ev: MouseEvent) => move(ev)
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  const toNow = useCallback(() => {
    setHour(null)
    setPlaying(false)
    setLive(true)
  }, [])

  const replay = useCallback(() => {
    setLive(false)
    setHour(0)
    setPlaying(true)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement
      if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) return
      if (e.key === ' ') {
        e.preventDefault()
        if (playing) setPlaying(false)
        else replay()
      }
      if (e.key === 'ArrowLeft') {
        setPlaying(false)
        setLive(false)
        setHour((h) => Math.max(0, (h ?? lastHour) - 1))
      }
      if (e.key === 'ArrowRight') {
        setPlaying(false)
        setLive(false)
        setHour((h) => Math.min(lastHour, (h ?? lastHour) + 1))
      }
      if (e.key === 'f' || e.key === 'F') {
        setImmersive((was) => !was)
      }
      if (e.key === 'Escape') {
        // The overlay first: it is the reader's most recent and most reversible action.
        if (immersiveRef.current) setImmersive(false)
        else if (selected) setSelected(null)
        else if (stationId) pickStation(null)
        else toNow()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, lastHour, selected, stationId, replay, toNow])

  const immersiveRef = useRef(false)
  immersiveRef.current = immersive

  const pickStation = (id: string | null) => {
    setStationId(id)
    const next = new URLSearchParams(params)
    if (id) next.set('station', id)
    else next.delete('station')
    setParams(next, { replace: true })
  }

  // ── render ─────────────────────────────────────────────────────────────────

  if (error && !floor) return <ErrorStrip message={error} />
  if (!floor) return <LoadingBlock label="Opening the floor" />

  const totals = floor.totals
  const selectedCell = floor.cells.find((c) => c.id === selected) ?? null
  const hoveredCell = floor.cells.find((c) => c.id === hovered) ?? null
  const attention = attentionCells(visible)
  const scrubbed = hour !== null && hour !== lastHour
  const asAtLabel = hours[index]
    ? new Date(hours[index] as string).toLocaleTimeString([], {
      hour: '2-digit', minute: '2-digit',
    })
    : '—'

  const board = (
    <div ref={rootRef}
         className={cn('axis-floor relative flex flex-col overflow-hidden',
           immersive
             ? 'h-dvh w-screen'
             : '-mx-3 -mb-3 h-[calc(100dvh-9.5rem)] min-h-[620px] border')}
         style={{ borderColor: 'var(--floor-grid)' }}>

      {/* ── masthead ── */}
      <header className="flex shrink-0 flex-wrap items-stretch border-b"
              style={{ borderColor: 'var(--floor-grid)' }}>
        <div className="flex items-center gap-3 border-r px-4 py-2.5"
             style={{ borderColor: 'var(--floor-grid)' }}>
          <Beacon live={live && floor.day.pollsOpen} />
          <span className="flex flex-col justify-center">
            <b className="text-[17px] leading-tight font-medium tracking-tight"
               style={{ color: 'var(--floor-ink)' }}>The Floor</b>
            <span className="axis-floor-eyebrow mt-px">
              {floor.day.electionName} · polling day
            </span>
          </span>
        </div>

        <div className="flex items-center gap-1 border-r px-2"
             style={{ borderColor: 'var(--floor-grid)' }}>
          <button type="button" onClick={replay} aria-pressed={playing}
                  className="axis-floor-toggle"
                  title="Replay the day from the open (space)">
            {playing ? '❚❚ Playing' : '▶ Replay the day'}
          </button>
          <button type="button" onClick={toNow} aria-pressed={live && !scrubbed}
                  className="axis-floor-toggle"
                  title="Back to the latest report, and resume polling">
            Now
          </button>
          <button type="button" aria-pressed={showTrails}
                  onClick={() => setShowTrails((v) => !v)} className="axis-floor-toggle"
                  title="Comet tails — where each cell was over the last few seconds">
            Trails
          </button>
          <button type="button" aria-pressed={immersive}
                  onClick={() => setImmersive((was) => !was)} className="axis-floor-toggle"
                  title={immersive
                    ? 'Leave full screen (Esc or F)'
                    : 'Full screen — hides the app chrome for the wall display (F)'}>
            {immersive ? '⤡ Minimise' : '⤢ Full screen'}
          </button>
        </div>

        {/* The elastic middle. Everything else in the masthead is a fixed size, so this is
            what gives when the window narrows — a picker and a sentence can shrink, a count
            cannot. */}
        <div className="flex min-w-[150px] flex-1 items-center gap-3 px-3">
          <label className="flex min-w-0 items-center gap-1.5">
            <span className="axis-floor-eyebrow shrink-0">Station</span>
            <select value={stationId ?? ''} onChange={(e) => pickStation(e.target.value || null)}
                    className="axis-floor-select min-w-0 flex-1">
              <option value="">Every station</option>
              {floor.stations.map((s) => (
                <option key={s.id} value={s.id}>{s.name ?? 'Unnamed'}</option>
              ))}
            </select>
          </label>
          <span className={cn('axis-floor-mini truncate', scrubbed && 'axis-floor-throb')}
                style={scrubbed ? { color: 'var(--floor-behind)' } : undefined}>
            {scrubbed
              ? `Held at ${asAtLabel} — the board is not following the day`
              : `As at ${asAtLabel}${floor.day.lastReportAt
                ? ` · last report ${new Date(floor.day.lastReportAt).toLocaleTimeString([],
                  { hour: '2-digit', minute: '2-digit' })}` : ''}`}
          </span>
        </div>

        {totals ? (
          <div className="flex shrink-0 items-center gap-4 border-l px-4"
               style={{ borderColor: 'var(--floor-grid)' }}>
            <Stat label="Voted" value={totals.voted} tone="ahead"
                  note="Members reported as having voted" />
            <Stat label="In queue" value={totals.inQueue} tone="queue"
                  note="At the station and not counted yet — waiting, not slow" />
            <Stat label="Still out" value={totals.outstanding} tone="behind"
                  note="Members with no report of a vote" />
            <Stat label="Behind" value={totals.cellsBehind} tone="behind"
                  note="Cells more than 10 points under the day's elapsed share" />
            <Stat label="Silent" value={totals.cellsStalled}
                  tone={totals.cellsStalled > 0 ? 'critical' : undefined}
                  pulse={totals.cellsStalled > 0}
                  note="Cells with no report for over 90 minutes and members still out" />
            <Countdown closesAt={floor.day.closesAt} pollsOpen={floor.day.pollsOpen} />
          </div>
        ) : null}
      </header>

      {floor.unavailable ? (
        <div className="flex flex-1 items-center justify-center px-10">
          <p className="max-w-lg text-center text-[13px] leading-relaxed"
             style={{ color: 'var(--floor-ink-dim)' }}>{floor.unavailable}</p>
        </div>
      ) : (
        <div className="grid min-h-0 flex-1"
             style={{ gridTemplateColumns: '1fr 336px', gap: 1,
                      background: 'var(--floor-grid)' }}>

          <div className="grid min-h-0 min-w-0"
               style={{ gridTemplateRows: '1fr auto', gap: 1,
                        background: 'var(--floor-grid)' }}>
            <div ref={stageRef} className="relative min-h-0 min-w-0"
                 style={{ background: 'var(--floor-bg)' }}>
              <canvas ref={trackRef} className="block size-full"
                      role="img"
                      aria-label={`${floor.cells.length} cells across `
                        + `${floor.stations.length} stations. `
                        + `${totals?.voted ?? 0} voted, ${totals?.inQueue ?? 0} in the queue, `
                        + `${totals?.outstanding ?? 0} still out. `
                        + `${totals?.cellsBehind ?? 0} cells behind pace.`}
                      onMouseMove={onTrackMove}
                      onMouseLeave={() => setHovered(null)}
                      onClick={onTrackClick} />
              {hoveredCell ? <Readout cell={hoveredCell} votedAt={votedAt} /> : null}
            </div>

            <div className="shrink-0" style={{ background: 'var(--floor-bg)' }}>
              <canvas ref={stripRef}
                      className="axis-floor-scrub block h-[112px] w-full"
                      onMouseDown={onScrub} />
              <p className="axis-floor-mini px-4 pb-2" style={{ paddingLeft: STRIP.left }}>
                Drag to scrub the day · space to replay · ← → an hour at a time ·
                bars are the hour's own count, the dashed line is a flat day
              </p>
            </div>
          </div>

          {/* ── the rail ── */}
          <aside className="axis-floor-rail min-h-0 overflow-y-auto">
            {selectedCell ? (
              <Panel title="Selected cell">
                <CellCard cell={selectedCell}
                          onOpen={(id) => navigate(`/station-mates/cells?cell=${id}`)}
                          onClear={() => setSelected(null)} />
              </Panel>
            ) : null}

            <Panel title="Escalations" tone="critical"
                   note={totals ? `${totals.escalationsLate} late` : undefined}
                   storageKey="escalations" maxBodyHeight={260}>
              <Escalations rows={floor.escalations} onPick={setSelected}
                           selectedCellId={selected} />
            </Panel>

            <Panel title="Needs a decision" note={`${attention.length}`}
                   storageKey="attention" maxBodyHeight={280}>
              <Attention cells={attention} onPick={setSelected} selectedCellId={selected} />
            </Panel>

            <Panel title="Stations" note={`${floor.stations.length}`}
                   storageKey="stations" maxBodyHeight={240}>
              <Stations stations={floor.stations} selectedId={stationId} onPick={pickStation} />
            </Panel>
          </aside>
        </div>
      )}

      <footer className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1 border-t px-4 py-2"
              style={{ borderColor: 'var(--floor-grid)' }}>
        <span className="axis-floor-mini">
          Polls {new Date(floor.day.opensAt).toLocaleTimeString([],
            { hour: '2-digit', minute: '2-digit' })}
          {' – '}
          {new Date(floor.day.closesAt).toLocaleTimeString([],
            { hour: '2-digit', minute: '2-digit' })}
        </span>
        <span className="axis-floor-mini">
          {totals?.cells ?? 0} cells · {totals?.members ?? 0} members ·{' '}
          {totals?.pledged ?? 0} committed
        </span>
        <span className="axis-floor-mini">
          {totals?.transportGaps ?? 0} with no way of getting there
        </span>
        <span className="axis-floor-mini">
          Dot size is members still out · inner blue ring is the queue · the sweeping rule is
          the pace a flat day would need
        </span>
        {error ? (
          <span className="axis-floor-mini" style={{ color: 'var(--floor-critical)' }}>
            Refresh failed · {error}
          </span>
        ) : null}
        <span className="axis-floor-mini ml-auto">
          {immersive ? 'Esc or F to leave full screen' : 'F for full screen'}
        </span>
      </footer>
    </div>
  )

  /**
   * Full screen is an in-app OVERLAY, and deliberately not the browser's Fullscreen API.
   *
   * What an operations room wants is the app's own chrome gone — sidebar, top bar, tab strip —
   * because on a wall display those are somebody else's navigation. An overlay does exactly
   * that, every time. Requesting native fullscreen alongside it cannot be kept in step: the
   * browser can refuse it, grant it, or exit it for reasons of its own, and each of those fires
   * a change event that drags the overlay with it. F11 still composes, because the two no
   * longer know about each other.
   *
   * Portalled to the body rather than raised with a z-index: the app shell has its own
   * `overflow: hidden`, which clips an in-place overlay and makes the takeover look half
   * applied.
   */
  return immersive
    ? createPortal(
      <div className="fixed inset-0 z-[200]" role="dialog"
           aria-label="The Floor, full screen">
        {board}
      </div>,
      document.body,
    )
    : board
}

/**
 * Cells that need somebody to decide something, worst first.
 *
 * Silent cells before slow ones: a cell reporting badly is a cell you can help, and a cell not
 * reporting at all is one you do not yet know about. The second is the more urgent call.
 */
function attentionCells(cells: FloorCell[]): FloorCell[] {
  return cells
    .filter((c) => c.outstanding > 0
      && ((c.stalledMinutes !== null && c.stalledMinutes > 90) || c.transportGaps > 0
        || c.behind > 0.15))
    .sort((a, b) => {
      const aSilent = a.stalledMinutes !== null && a.stalledMinutes > 90 ? 1 : 0
      const bSilent = b.stalledMinutes !== null && b.stalledMinutes > 90 ? 1 : 0
      if (aSilent !== bSilent) return bSilent - aSilent
      return b.outstanding - a.outstanding
    })
    .slice(0, 40)
}

/** The hover readout. Positioned in the DOM so it inherits the app's type and can wrap. */
function Readout({ cell, votedAt }: { cell: FloorCell, votedAt: Map<string, number> }) {
  const voted = votedAt.get(cell.id) ?? cell.voted
  const out = Math.max(0, cell.members - voted)
  return (
    <div className="axis-floor-in pointer-events-none absolute z-10 min-w-[190px] max-w-[260px]
                    border px-3 py-2"
         style={{
           left: TRACK.left + 12,
           top: 44,
           borderColor: 'var(--floor-grid)',
           background: 'color-mix(in oklab, var(--floor-panel) 94%, transparent)',
         }}>
      <b className="mb-1 flex items-center gap-2 text-[12px]"
         style={{ color: 'var(--floor-ink)' }}>
        <span className="axis-floor-code">{cell.code}</span>
        {cell.label ?? 'Unnamed'}
      </b>
      <span className="axis-floor-mini block">
        {cell.stationName ?? 'unknown station'}
        {cell.meetingPoint ? ` · ${cell.meetingPoint}` : ''}
      </span>
      <span className="mt-1.5 block text-[11px]" style={{ color: 'var(--floor-ink-dim)' }}>
        <b className="font-mono font-normal" style={{ color: 'var(--floor-ahead)' }}>{voted}</b>
        {' of '}{cell.members} voted ·{' '}
        <b className="font-mono font-normal" style={{ color: 'var(--floor-behind)' }}>{out}</b>
        {' still out'}
        {cell.inQueue > 0 ? (
          <>{' · '}<b className="font-mono font-normal"
                      style={{ color: 'var(--floor-queue)' }}>{cell.inQueue}</b> queuing</>
        ) : null}
      </span>
      {cell.stalledMinutes !== null && cell.stalledMinutes > 90 ? (
        <span className="mt-1 block text-[10.5px]" style={{ color: 'var(--floor-critical)' }}>
          No report for {cell.stalledMinutes} minutes.
        </span>
      ) : null}
    </div>
  )
}

/**
 * The live beacon.
 *
 * It pulses only while the board is genuinely following the day. Stopped is a state somebody
 * has to be able to see from across a room, and a beacon that pulses regardless is a beacon
 * that means nothing.
 */
function Beacon({ live }: { live: boolean }) {
  return (
    <span className="relative flex size-7 items-center justify-center">
      <span className={cn('absolute inset-0 rounded-full border', live && 'axis-floor-throb')}
            style={{ borderColor: live ? 'var(--floor-ahead)' : 'var(--floor-grid)' }} />
      <span className="block size-2 rounded-full"
            style={{ background: live ? 'var(--floor-ahead)' : 'var(--floor-ink-faint)' }} />
    </span>
  )
}
